import { useMemo } from 'react'
import useDebounce from '@/hooks/useDebounce'
import useAsync from '@/hooks/useAsync'
import useChainId from '@/hooks/useChainId'
import { mirrorNodeFetch } from '@/services/mirrorNodeCache'

interface AccountResponse {
  account: string
  evm_address: string
}

const useAccountResolver = (
  value?: string,
  chainIdOverride?: string,
): { address: string | undefined; resolverError?: Error; resolving: boolean } => {
  const debouncedValue = useDebounce((value || '').trim(), 200)
  const defaultChainId = useChainId()
  const chainId = chainIdOverride || defaultChainId

  const [response, resolverError, isResolving] = useAsync<{ address: string } | undefined>(() => {
    if (!debouncedValue) return
    return mirrorNodeFetch<AccountResponse>(`/api/v1/accounts/${debouncedValue}`, chainId).then((data) =>
      data ? { address: data.evm_address } : undefined,
    )
  }, [debouncedValue, chainId])

  const resolving = isResolving && !!debouncedValue
  const address = response && response.address

  return useMemo(
    () => ({
      address,
      resolverError,
      resolving,
    }),
    [address, resolverError, resolving],
  )
}

export default useAccountResolver
